import { cn } from "@/lib/utils";
import { panelRadiusClass } from "../chat/branding";
import { PhoneInput } from "@/components/molecules/phone";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
} from "@/components/molecules/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePublicTranslation } from "@/features/utils/i18n";
import { useEffect, useState } from "react";
import {
  type UpdateCustomerError,
  type UpdateCustomerInput,
  usePublicCustomer,
  useUpdatePublicCustomer,
} from "../../hooks/use-public-customer";

interface ConfirmCustomerInfoProps {
  publicId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The visitor opened this themselves to edit their details, rather than being asked to
   * complete them before the conversation can go on. Nothing is required of them, so the
   * dialog closes like any other — locking it behind a mandatory submit is only right for
   * the prompt they did not ask for.
   */
  dismissible?: boolean;
}

export function ConfirmCustomerInfo({
  publicId,
  open,
  onOpenChange,
  dismissible = false,
}: ConfirmCustomerInfoProps) {
  const { t } = usePublicTranslation();
  const { data: customer, isLoading } = usePublicCustomer(publicId);
  const updateMutation = useUpdatePublicCustomer(publicId);

  const [formData, setFormData] = useState<UpdateCustomerInput>({
    given_name: "",
    family_name: "",
    phone: "",
    email: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  // A contact obtained from the transport layer (the number they are calling from, a
  // phone proven by OTP, the address an email came in on) identifies the channel itself
  // — the backend refuses to overwrite it, so it must not look editable here. The server
  // is the source of truth for that; both fields can be locked at once.
  const phoneLocked = !!customer?.phone_is_locked;
  const emailLocked = !!customer?.email_is_locked;

  // Consent is recorded on the customer, so it is asked once and only once — coming back
  // to fix a typo in a name is not a new agreement to obtain.
  const termsAlreadyAccepted = !!customer?.terms_accepted_at;

  // Update form data when customer data loads
  useEffect(() => {
    if (customer) {
      setFormData({
        given_name: customer.given_name || "",
        family_name: customer.family_name || "",
        phone: customer.phone || "",
        email: customer.email || "",
      });
    }
  }, [customer]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    setErrors({});

    // Validate email
    if (!formData.email || !validateEmail(formData.email)) {
      setErrors({ email: t("confirm_info.invalid_email") });
      return;
    }

    // Validate phone
    if (!formData.phone) {
      setErrors({ phone: t("confirm_info.invalid_phone") });
      return;
    }

    // Validate privacy and terms acceptance
    if (!termsAlreadyAccepted && !acceptedPrivacy) {
      setErrors({
        general: t("confirm_info.accept_terms_required"),
      });
      return;
    }

    try {
      await updateMutation.mutateAsync({
        ...formData,
        ...(termsAlreadyAccepted ? {} : { accept_terms: true }),
      });
      onOpenChange(false);
    } catch (error) {
      const err = error as UpdateCustomerError;
      if (err.feedback) {
        // The backend answers with codes for the cases it knows how to phrase; anything
        // else is already human-readable validation feedback.
        setErrors(
          Object.fromEntries(
            Object.entries(err.feedback).map(([field, message]) => [
              field,
              message === "contact_locked"
                ? t("confirm_info.contact_locked")
                : message,
            ]),
          ),
        );
      }
    }
  };

  const handleChange = (field: keyof UpdateCustomerInput, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={dismissible ? onOpenChange : () => {}}
    >
      <ResponsiveDialogContent
        className={cn("sm:max-w-[425px]", panelRadiusClass)}
        showCloseButton={dismissible}
      >
        <ResponsiveDialogHeader>
          <h2 className="text-lg font-semibold">
            {dismissible ? t("confirm_info.title") : t("confirm_info.welcome")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {dismissible
              ? t("confirm_info.description")
              : t("confirm_info.validation_required")}
          </p>
        </ResponsiveDialogHeader>

        <div className="px-4 space-y-4">
          {isLoading ? (
            <div className="text-center py-4 text-muted-foreground">
              {t("confirm_info.loading")}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="given_name">
                    {t("confirm_info.first_name")}
                  </Label>
                  <Input
                    placeholder={t("confirm_info.first_name_placeholder")}
                    id="given_name"
                    value={formData.given_name}
                    onChange={(e) => handleChange("given_name", e.target.value)}
                    aria-invalid={!!errors.given_name}
                  />
                  {errors.given_name && (
                    <p className="text-xs text-destructive">
                      {errors.given_name}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="family_name">
                    {t("confirm_info.last_name")}
                  </Label>
                  <Input
                    placeholder={t("confirm_info.last_name_placeholder")}
                    id="family_name"
                    value={formData.family_name}
                    onChange={(e) =>
                      handleChange("family_name", e.target.value)
                    }
                    aria-invalid={!!errors.family_name}
                  />
                  {errors.family_name && (
                    <p className="text-xs text-destructive">
                      {errors.family_name}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t("confirm_info.phone")}</Label>
                <PhoneInput
                  placeholder="+33 6 12 34 56 78"
                  id="phone"
                  value={formData.phone}
                  onChange={(value) => handleChange("phone", value || "")}
                  aria-invalid={!!errors.phone}
                  disabled={phoneLocked}
                />
                {phoneLocked && (
                  <p className="text-xs text-muted-foreground">
                    {t("confirm_info.contact_locked")}
                  </p>
                )}
                {errors.phone && (
                  <p className="text-xs text-destructive">{errors.phone}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t("confirm_info.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  aria-invalid={!!errors.email}
                  placeholder="name@server.com"
                  disabled={emailLocked}
                />
                {emailLocked && (
                  <p className="text-xs text-muted-foreground">
                    {t("confirm_info.contact_locked")}
                  </p>
                )}
                {errors.email && (
                  <p className="text-xs text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-3 pt-4 border-t">
                {termsAlreadyAccepted ? (
                  <p className="text-xs text-muted-foreground">
                    {t("confirm_info.terms_already_accepted")}
                  </p>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="privacy"
                      checked={acceptedPrivacy}
                      onCheckedChange={(checked) =>
                        setAcceptedPrivacy(checked === true)
                      }
                    />
                    <label
                      htmlFor="privacy"
                      className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t("confirm_info.accept_privacy_prefix")}{" "}
                      <a
                        href="https://heyup.ai/en/legal/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline hover:no-underline"
                      >
                        {t("confirm_info.privacy_policy")}
                      </a>
                      {` ${t("confirm_info.and_the")} `}
                      <a
                        href="https://heyup.ai/en/legal/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline hover:no-underline"
                      >
                        {t("confirm_info.terms_of_service")}
                      </a>
                    </label>
                  </div>
                )}

                {errors.general && (
                  <p className="text-xs text-destructive">{errors.general}</p>
                )}
              </div>
            </>
          )}
        </div>

        <ResponsiveDialogFooter className="flex justify-center gap-2">
          {dismissible && (
            <Button
              size="lg"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              {t("confirm_info.cancel")}
            </Button>
          )}
          <Button
            size="lg"
            className="w-48"
            onClick={handleSubmit}
            disabled={updateMutation.isPending || isLoading}
          >
            {updateMutation.isPending
              ? t("confirm_info.saving")
              : t("confirm_info.confirm")}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
